// The dice tray. A panel with a canvas of six real dice (DiceTrayRig). Press
// on one and it lifts and follows the pointer inside the canvas; carry it past
// the panel edge and a real board die takes over under the cursor
// (diceTrayDrag); let go inside the panel and it goes back. Nothing ever
// spawns from inside.
import { Component } from 'preact';

import { CloseButton } from '../ui/navButtons';
import { UI, playUI } from '../../utils/arena/uiSounds';
import { DiceTrayRig, traySlots } from '../../utils/game/dice3d/trayRig';
import DiceAppearancePicker, { DICE_PANEL_SURFACE } from '../DiceAppearancePicker';
import { DICE_CONFIGS } from '../../utils/game/diceMesh';
import { TEXT_BODY, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { t } from '../../localization/t';

const PANEL_STYLE = {
  ...DICE_PANEL_SURFACE,
  width: '19cqw',
  minWidth: '270px',
  padding: '0.75cqw',
  gap: '0.25cqw',
  zIndex: 1050,
};

export default class DiceTrayDrawer extends Component {
  state = { slots: [], dragging: null };

  componentDidMount() {
    if (this.props.open) this.mountRig();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) this.mountRig();
    if (prevProps.open && !this.props.open) this.unmountRig();
  }

  componentWillUnmount() {
    this.unmountRig();
  }

  mountRig() {
    if (this.rig || !this.canvas) return;
    this.rig = new DiceTrayRig(this.canvas);
    this.props.onRig?.(this.rig);
    this.measure();
    this.ro = new ResizeObserver(this.measure);
    this.ro.observe(this.canvas);
  }

  unmountRig() {
    this.ro?.disconnect();
    this.ro = null;
    this.unbind();
    if (this.rig) {
      this.props.onRig?.(null);
      this.rig.dispose();
      this.rig = null;
    }
  }

  // The canvas draws at the display's resolution; the labels share its pixel slots.
  measure = () => {
    if (!this.canvas || !this.rig) return;
    const r = this.canvas.getBoundingClientRect();
    if (r.width === 0) return;
    this.rig.setSize(Math.round(r.width), Math.round(r.height), Math.min(2, window.devicePixelRatio || 1));
    this.setState({ slots: traySlots(r.width, r.height) });
  };

  bind() {
    window.addEventListener('mousemove', this.onWindowMove);
    window.addEventListener('mouseup', this.onWindowUp);
  }

  unbind() {
    window.removeEventListener('mousemove', this.onWindowMove);
    window.removeEventListener('mouseup', this.onWindowUp);
  }

  onFrameMove = (e) => {
    if (this.drag || !this.rig) return;
    const hit = this.rig.hitTest(e.clientX, e.clientY);
    if (hit && hit !== this.rig.hovered) playUI(UI.HOVER, { volume: 0.4 });
    this.rig.setHover(hit);
  };

  onFrameLeave = () => this.rig?.setHover(null);

  onFrameDown = (e) => {
    const dieType = this.rig?.hitTest(e.clientX, e.clientY);
    if (!dieType) return;
    e.preventDefault();
    e.stopPropagation();
    this.drag = { dieType, onBoard: false };
    this.rig.setHover(null);
    this.rig.hold(dieType, e.clientX, e.clientY);
    playUI(UI.UNEQUIP);
    this.setState({ dragging: dieType });
    this.bind();
  };

  overTray(e) {
    const r = this.panel?.getBoundingClientRect();
    return !!r && e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  }

  onWindowMove = (e) => {
    const drag = this.drag;
    if (!drag) return;
    if (this.overTray(e)) {
      if (drag.onBoard) {
        this.props.onDragEnd(e, { cancel: true });
        drag.onBoard = false;
      }
      this.rig?.hold(drag.dieType, e.clientX, e.clientY);
    } else if (drag.onBoard) {
      this.props.onDragMove(e);
    } else if (this.props.onDragStart(drag.dieType, e)) {
      drag.onBoard = true;
      this.rig?.putDown();
    }
  };

  onWindowUp = (e) => {
    this.unbind();
    const drag = this.drag;
    this.drag = null;
    this.rig?.putDown();
    this.setState({ dragging: null });
    if (drag?.onBoard) this.props.onDragEnd(e, { cancel: this.overTray(e) });
  };

  setPanel = (el) => { this.panel = el; };

  setCanvas = (el) => { this.canvas = el; };

  render() {
    const { open, onClose } = this.props;
    const { slots } = this.state;
    if (!open) return null;
    return (
      <div data-dice-tray ref={this.setPanel} className="absolute left-12 top-0 flex flex-col" style={PANEL_STYLE}>
        <div className="relative flex items-center justify-between" style={{ paddingLeft: '0.25cqw' }}>
          <div className="arena-heading text-[0.6875cqw] font-bold" style={{ color: TEXT_PRIMARY }}>{t('Dice')}</div>
          <DiceAppearancePicker />
          <CloseButton ghost onClick={onClose} title={t('Close')} />
        </div>
        <div
          className="relative w-full"
          onMouseMove={this.onFrameMove}
          onMouseLeave={this.onFrameLeave}
          onMouseDown={this.onFrameDown}
        >
          <canvas ref={this.setCanvas} className="block w-full" style={{ aspectRatio: '17 / 11' }} />
          {slots.map((a) => (
            <div key={a.dieType}>
              <div data-die-anchor={a.dieType} className="absolute size-px" style={{ left: a.x, top: a.y }} />
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2 text-[0.6cqw] font-semibold pointer-events-none"
                style={{ left: a.labelX, top: a.labelY, color: TEXT_BODY, textShadow: '0 1px 0.125cqw rgba(0,0,0,0.9)' }}
              >
                {DICE_CONFIGS[a.dieType].label}
              </div>
            </div>
          ))}
        </div>
        <div className="text-[0.55cqw] text-center" style={{ color: TEXT_BODY }}>Drag a die onto the table</div>
      </div>
    );
  }
}
